<template>
    <div class="modal is-active" v-if="show">
        <div class="modal-background" @click="cancel"></div>
        <div class="modal-card kb-channel-modal" :class="{'kb-channel-modal--wide': isWideLayout}">
            <header class="modal-card-head">
                <p class="modal-card-title">{{ title }}</p>
                <button class="delete" @click.prevent="cancel"></button>
            </header>
            <section class="modal-card-body">
                <!-- Step 1 (connect only): pick a channel. -->
                <template v-if="mode === 'connect' && selectedChannel == null">
                    <p class="kb-channel-modal__intro">{{ $t('knowledgebase.channelPickIntro') }}</p>
                    <ul class="kb-channel-modal__list">
                        <li
                            v-for="ch in availableChannels"
                            :key="ch.id"
                            class="kb-channel-modal__item"
                            @click="selectChannel(ch)"
                        >
                            <div class="kb-channel-modal__item-icon">
                                {{ (ch.name[0] || '?').toUpperCase() }}
                            </div>
                            <div class="kb-channel-modal__item-body">
                                <div class="kb-channel-modal__item-name">{{ ch.name }}</div>
                                <div class="kb-channel-modal__item-type">{{ ch.integration_type }}</div>
                            </div>
                        </li>
                        <li v-if="availableChannels.length === 0" class="kb-channel-modal__empty">
                            {{ $t('knowledgebase.channelAllConnected') }}
                        </li>
                    </ul>
                </template>

                <!-- Step 2: configure. Shared between connect and configure flows. -->
                <template v-else>
                    <div class="kb-channel-modal__configure-head">
                        <div class="kb-channel-modal__item-icon">
                            {{ (channelName[0] || '?').toUpperCase() }}
                        </div>
                        <div>
                            <div class="kb-channel-modal__item-name">{{ channelName }}</div>
                            <div class="kb-channel-modal__item-type">{{ integrationType }}</div>
                        </div>
                    </div>

                    <component
                        v-if="configComponent"
                        :is="configComponent"
                        ref="configForm"
                        :value="config"
                        :connection="existingConnection"
                        :summary="summary"
                        @input="onConfigChange"
                    />
                    <!-- No per-integration config form (e.g. generic_pull): the
                         one thing worth surfacing is the connection's own
                         api_key, since a pull-style third party needs it to
                         call us — but only once the connection actually
                         exists (configure mode), not mid-connect. -->
                    <div v-else-if="mode === 'configure'" class="kb-channel-modal__api-key">
                        <label class="label">{{ $t('knowledgebase.channelApiKey') }}</label>
                        <div class="kb-channel-modal__api-key-row">
                            <code class="kb-channel-modal__api-key-value">{{ existingConnection.api_key }}</code>
                            <button
                                type="button"
                                class="button is-small is-outlined"
                                @click="copyApiKey"
                            >
                                {{ keyCopied ? $t('knowledgebase.channelApiKeyCopied') : $t('knowledgebase.channelApiKeyCopy') }}
                            </button>
                        </div>
                        <p class="kb-channel-modal__api-key-help">
                            {{ $t('knowledgebase.channelApiKeyHelp') }}
                        </p>
                        <button
                            type="button"
                            class="button is-small is-outlined"
                            :disabled="rotatePending"
                            :class="{'is-loading': rotatePending}"
                            @click="rotateApiKey"
                        >
                            {{ $t('knowledgebase.channelApiKeyRotate') }}
                        </button>
                    </div>
                    <p v-else class="kb-channel-modal__no-config">
                        {{ $t('knowledgebase.channelNoConfigNeeded') }}
                    </p>
                </template>
            </section>
            <footer class="modal-card-foot">
                <button
                    v-if="mode === 'connect' && selectedChannel != null"
                    class="button is-primary"
                    :class="{'is-loading': busy}"
                    :disabled="!canSubmit || busy"
                    @click="submitConnect"
                >
                    {{ $t('knowledgebase.channelConnect') }}
                </button>
                <button
                    v-if="mode === 'configure'"
                    class="button is-primary"
                    :class="{'is-loading': busy}"
                    :disabled="!canSubmit || busy"
                    @click="submitConfigure"
                >
                    {{ $t('generic.save') }}
                </button>
                <button
                    v-if="mode === 'configure'"
                    class="button is-danger is-outlined"
                    :disabled="busy"
                    @click="disconnect"
                >
                    {{ $t('knowledgebase.channelDisconnect') }}
                </button>
                <button class="button" @click="cancel">{{ $t('generic.cancel') }}</button>
            </footer>
        </div>
    </div>
</template>

<script>
import { Api } from '../../../api';
import KbConfigSynthflow from './kb-config-synthflow.vue';

// integration_type -> Vue component to render its config form.
// New integration_type = add a sibling component and a row here.
const CONFIG_COMPONENTS = {
    synthflow: KbConfigSynthflow,
};

export default {
    name: 'kb-channel-modal',

    components: {KbConfigSynthflow},

    data() {
        return {
            show: false,
            busy: false,
            // 'connect' (pick a channel + configure) or 'configure' (edit existing).
            mode: 'connect',
            allChannels: [],
            // Channel ids already connected — used to filter the step-1 list.
            connections: [],
            // Connect-flow: the channel chosen in step 1. Configure-flow: null,
            // because we're editing an existing ChannelConnection (see below).
            selectedChannel: null,
            existingConnection: null,
            // Latest value emitted from the per-integration_type config component.
            config: {},
            // Read-only snapshot of campground-side facts the parent supplies
            // on openConfigure (article visibility counts, pending publish
            // count). null for the connect flow — there's no connection to
            // summarise yet.
            summary: null,
            // Feedback state for the api_key display (configure mode, no
            // per-integration config component).
            keyCopied: false,
            rotatePending: false,
        };
    },

    computed: {
        title() {
            if(this.mode === 'configure') return this.$t('knowledgebase.channelConfigureTitle');
            if(this.selectedChannel) {
                return this.$t('knowledgebase.channelConnectStep2', {name: this.selectedChannel.name});
            }
            return this.$t('knowledgebase.channelConnectStep1');
        },
        channelName() {
            if(this.selectedChannel) return this.selectedChannel.name;
            return this.existingConnection ? this.existingConnection.channel_name : '';
        },
        integrationType() {
            if(this.selectedChannel) return this.selectedChannel.integration_type;
            return this.existingConnection ? this.existingConnection.integration_type : '';
        },
        configComponent() {
            return CONFIG_COMPONENTS[this.integrationType] || null;
        },
        isWideLayout() {
            // Only Synthflow benefits from the two-column layout today.
            // Other integrations stay at the original 540px width.
            return this.integrationType === 'synthflow';
        },
        availableChannels() {
            const connectedIds = new Set(this.connections.map(c => c.channel));
            return this.allChannels.filter(c => !connectedIds.has(c.id));
        },
        canSubmit() {
            // No config component = nothing to validate, always submittable.
            if(!this.configComponent) return true;
            // Validate from `this.config` (the value the child emits) rather
            // than calling into `$refs.configForm.isValid()`. Vue 2's
            // reactivity doesn't track method calls on child refs, so the
            // button wouldn't re-enable when the child's internal state
            // changed. Reading from a reactive prop fixes that.
            //
            // Rules per integration_type — keep these in sync with each
            // config component. We could push this into a `validate(config)`
            // export on each child to avoid the duplication, but with one
            // integration today the inline check is more legible.
            if(this.integrationType === 'synthflow') {
                if(!this.config.instance_url) return false;
                if(!this.config.assistant_id) return false;
                if(!this.config.api_key) return false;
                if(this.config.portal_id == null) return false;
                return true;
            }
            return true;
        },
    },

    methods: {
        async openConnect({connections}) {
            this.mode = 'connect';
            this.selectedChannel = null;
            this.existingConnection = null;
            this.config = {};
            this.summary = null;
            this.connections = connections || [];
            this.show = true;
            this.busy = true;
            try {
                const res = await Api.knowledgebase.fetchChannels();
                this.allChannels = this._results(res);
            } finally {
                this.busy = false;
            }
        },
        openConfigure(connection, summary = null) {
            this.mode = 'configure';
            this.selectedChannel = null;
            this.existingConnection = connection;
            // Surface the top-level FK as `portal_id` inside the form value so
            // the child config component can edit it alongside `config.*`
            // fields. We split it back out in submitConfigure().
            this.config = {...(connection.config || {}), portal_id: connection.portal};
            this.summary = summary;
            this.show = true;
        },
        _results(res) {
            const body = res.body;
            return body && body.results ? body.results : (body || []);
        },
        selectChannel(channel) {
            this.selectedChannel = channel;
            this.config = {};
        },
        onConfigChange(value) {
            this.config = value;
        },
        // `portal_id` is emitted by the config component for ergonomics (it
        // sits next to the other fields on screen), but on the wire it's the
        // top-level `portal` FK on the connection — not part of `config`.
        // Split it back out before hitting the API.
        _splitPortal(form) {
            // eslint-disable-next-line camelcase
            const {portal_id: portalId, ...config} = form || {};
            return {portal: portalId == null ? null : portalId, config};
        },
        async submitConnect() {
            this.busy = true;
            try {
                const {portal, config} = this._splitPortal(this.config);
                await Api.knowledgebase.createConnection({
                    channel: this.selectedChannel.id,
                    portal,
                    config,
                });
                this.$emit('changed');
                this.show = false;
            } finally {
                this.busy = false;
            }
        },
        async submitConfigure() {
            this.busy = true;
            try {
                const {portal, config} = this._splitPortal(this.config);
                await Api.knowledgebase.updateConnection(this.existingConnection.id, {
                    portal,
                    config,
                });
                this.$emit('changed');
                this.show = false;
            } finally {
                this.busy = false;
            }
        },
        async disconnect() {
            // Hard delete — connections are account-level, not publish-gated.
            // Confirm here rather than inline because losing a connection
            // is non-reversible from the UI (user has to reconnect + re-paste
            // credentials).
            if(!confirm(this.$t('knowledgebase.channelDisconnectConfirm'))) return;
            this.busy = true;
            try {
                await Api.knowledgebase.deleteConnection(this.existingConnection.id);
                this.$emit('changed');
                this.show = false;
            } finally {
                this.busy = false;
            }
        },
        cancel() {
            this.show = false;
        },
        async copyApiKey() {
            await navigator.clipboard.writeText(this.existingConnection.api_key);
            this.keyCopied = true;
            setTimeout(() => { this.keyCopied = false; }, 2000);
        },
        async rotateApiKey() {
            // Confirm here, not inline: rotating invalidates the current key
            // immediately (backend 404s on it right away), so anyone already
            // configured with the old key breaks until they're given the new
            // one — same irreversible-from-the-UI reasoning as disconnect().
            if(!confirm(this.$t('knowledgebase.channelApiKeyRotateConfirm'))) return;
            this.rotatePending = true;
            try {
                const res = await Api.knowledgebase.rotateApiKey(this.existingConnection.id);
                this.existingConnection = res.body;
            } finally {
                this.rotatePending = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.kb-channel-modal {
    width: 540px;
    max-width: 95vw;

    // Synthflow's two-section form benefits from a wider modal on desktop so
    // Connection and Campground data sit side by side. Other integrations
    // keep the original single-column width.
    &--wide {
        width: 920px;
    }

    &__intro {
        margin-bottom: 12px;
        color: #555;
    }

    &__list {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    &__item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        border: 1px solid #eee;
        border-radius: 4px;
        margin-bottom: 6px;
        cursor: pointer;

        &:hover {
            border-color: #b9d;
            background: #f8f9fc;
        }
    }

    &__item-icon {
        width: 36px;
        height: 36px;
        border-radius: 4px;
        background: #eaf3f8;
        color: #357;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
    }

    &__item-name {
        font-weight: 700;
    }

    &__item-type {
        font-size: 0.75rem;
        color: #888;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    &__empty {
        color: #888;
        text-align: center;
        padding: 24px 0;
        font-style: italic;
    }

    &__configure-head {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #eee;
    }

    &__no-config {
        color: #888;
        font-style: italic;
        text-align: center;
        padding: 12px 0;
    }

    &__api-key {
        padding: 4px 0 12px;
    }

    &__api-key-row {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__api-key-value {
        flex: 1;
        min-width: 0;
        overflow-x: auto;
        white-space: nowrap;
        background: #f3f3f3;
        border-radius: 4px;
        padding: 6px 10px;
        font-size: 0.85rem;
    }

    &__api-key-help {
        color: #888;
        font-size: 0.8rem;
        margin: 8px 0 12px;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/pages/knowledge-base/kb-channel-modal.vue